import { DbMongoBaseInfos } from "./db-mongo-base-infos";

import { ObjectLayer } from "./object-layer";
import { BaseMetadataType } from "./base-metadata-type";
import { MetadataFieldValueCardinality } from "./metadata-field-value-cardinality";
import { MetadataFieldValueObjectTypes } from "./metadata-field-value-object-types";
import { FormatStyle } from "./format-style";
import { TitleType } from "./title-type";
import { DbMetadataDynamicCollectionInfos } from "./db-metadata-dynamic-collection-infos";
import { DbObjectCreateOrUpdateInfos } from "./db-object-create-or-update-infos";
import { ObjectStatus } from "./object-status";
import { ObjectType } from "./object-type";
import { DbMetadataFieldValueInfos, MetadataFieldValueInfosCache } from "./db-metadata-field-value-infos";
import { immerable } from "immer";
import { ObjectClass } from "./object-class";

export class MetadataFieldInfosCache extends DbMongoBaseInfos {
  [immerable] = true;
  srch: boolean = true;
  mdtr: boolean = false;
  pop: boolean = false;
  bndlmkr: boolean = false;
  lr: ObjectLayer | null = null;
  bt: BaseMetadataType | null = null;
  ot: MetadataFieldValueObjectTypes | null = null;
  lb: string | null = null;
  tn: string | null = null;
  v: MetadataFieldValueInfosCache[] | null = null;
  oc?: ObjectClass[] | null = null;

  constructor(init?: Partial<MetadataFieldInfosCache>) {
    super();
    Object.assign(this, init);
  }
}

export class DbMetadataFieldInfos extends DbMongoBaseInfos {
  [immerable] = true;
  $type?: any | null | undefined;
  daletDataFieldTagName: string | null = null;
  isSearchable: boolean = true;
  isEditable: boolean = false;
  isMandatory: boolean = false;
  isInternal: boolean = false;
  allowPopulate: boolean = false;
  searchPatternEnabled: boolean = false;
  disabled: boolean = false;
  isUsedOnBundleMaker: boolean = false;
  platformId: number | null = 0;
  layer: ObjectLayer | null = null;
  baseType: BaseMetadataType | null = null;
  valuesCardinality: MetadataFieldValueCardinality | null = null;
  objectType: MetadataFieldValueObjectTypes | null = null;
  label: string | null = null;
  group: string | null = null;
  tagName: string | null = null;
  regexValue: string | null = null;
  minLength: number | null = 0;
  maxLength: number | null = 0;
  formatStyle: FormatStyle | null = null;
  titleType: TitleType | null = null;
  valuesDynamicCollection: DbMetadataDynamicCollectionInfos | null = null;
  creationInfos: DbObjectCreateOrUpdateInfos | null = null;
  objectStatuses: ObjectStatus[] | null = null;
  objectTypes: ObjectType[] | null = null;
  values: DbMetadataFieldValueInfos[] | null = null;
  updateInfos: DbObjectCreateOrUpdateInfos[] | null = null;
  disableInfos: DbObjectCreateOrUpdateInfos[] | null = null;
  value?: any | null = null;
  objectClasses?: ObjectClass[] | null = null;
  prohibitTrueToFalse: boolean = false;
  indexFalseBool: boolean | null = null;
  index?: number | null; // Front only;

  constructor(init?: Partial<DbMetadataFieldInfos>) {
    super();
    Object.assign(this, init);
  }
}

export enum SystemFieldsTagNames {
  clipInfo = "clipInfo", // Clip
  dcpCplName = "dcpCplName", // String
  videoProfile = "videoProfile", // String
  ingestQCType = "ingestQCType", // String
  followedByUserId = "followedByUserId", // Long
  contentType = "contentType", // String
  dcpContentType = "dcpContentType", // String
  contentEdit = "contentEdit", // String
  contentExploitation = "contentExploitation", // String
  contentSubType = "contentSubType", // String
  titleType = "titleType", // String
  titleSecondaryOriginalLanguages = "titleSecondaryOriginalLanguages", // List<LanguageCode>
  isDisabled = "isDisabled", // Bool
  serieEpisodeNumberProduction = "serieEpisodeNumberProduction", // Long
  serieSeasonEpisodeTotalNumber = "serieSeasonEpisodeTotalNumber", // Long
  serieSeasonNumber = "serieSeasonNumber", // Long
  programPartNumber = "programPartNumber", // Long
  programPartTotal = "programPartTotal", // Long
  originalPictureFormat = "originalPictureFormat", // String (Asset metadata)
  titleOriginalPictureFormat = "titleOriginalPictureFormat", // String (Title metadata)
  spockType = "spockType", // String
  productionDate = "productionDate", // DateTime
  fileListing = "fileListing", // List<String>
  dcpType = "dcpType", // String
  dcpIsCrypted = "dcpIsCrypted", // Bool
  dcpVersion = "dcpVersion", // String
  originalFileName = "originalFileName", // String
  dcpFileContent = "dcpFileContent", // List<String>
  durationSMPTE = "durationSMPTE", // String
  originalOwner = "originalOwner", // String
  pictureOverallCinemaMode = "pictureOverallCinemaMode", // String
  pictureOverallVersion = "pictureOverallVersion", // String
  stereoscopic = "stereoscopic", // Bool
  stereoscopicType = "stereoscopicType", // String
  languageAudio = "languageAudio", // LanguageCode
  territories = "territories", // String
  languageAudioSecondary = "languageAudioSecondary", // LanguageCode
  languageTitle = "languageTitle", // LanguageCode
  languagePicture = "languagePicture", // LanguageCode
  languageSubtitle = "languageSubtitle", // LanguageCode
  languageSubtitleLocalizedInput = "languageSubtitleLocalizedInput", // String
  languageSubtitleLocalizedOutput = "languageSubtitleLocalizedOutput", // String
  titleOriginalLanguage = "titleOriginalLanguage", // LanguageCode
  audioMix = "audioMix", // String
  audioMixOverallAccess = "audioMixOverallAccess", // String
  audioMixOverallVersion = "audioMixOverallVersion", // String
  audioOverallVersion = "audioOverallVersion", // String
  subtitleIsClosed = "subtitleIsClosed", // Bool
  subtitleIsBurned = "subtitleIsBurned", // Bool
  subtitleType = "subtitleType", // String
  subtitleSME = "subtitleSME", // Bool
  subtitleSMEType = "subtitleSMEType", // String
  subtitleClosedType = "subtitleClosedType", // String
  subtitleStandard = "subtitleStandard", // String
  subtitleGeneralType = "subtitleGeneralType", // String
  subtitleSeparatedPartialFile = "subtitleSeparatedPartialFile", // Bool
  subtitleSeparatedPartialFileFormat = "subtitleSeparatedPartialFileFormat", // String
  subtitles = "subtitles", // List<SubtitleData>
  dcpCplNames = "dcpCplNames", // List<String>
  doviVersion = "doviVersion", // string
  hdrMaxFall = "hdrMaxFall", // Double
  hdrMaxCLL = "hdrMaxCLL", // Double
  hdrType = "hdrType", // String
  isHdr = "isHdr", // Bool
  techFrameRate = "techFrameRate", // Double
  techWrapper = "techWrapper", // String
  techChromaSampling = "techChromaSampling", // String
  techCodec = "techCodec", // String
  techColorSpace = "techColorSpace", // String
  techColorSpaceTransfer = "techColorSpaceTransfer", // String
  techVideoCodingProfile = "techVideoCodingProfile", // String
  techBitRate = "techBitRate", // Double
  techScanType = "techScanType", // String
  techWidth = "techWidth", // Long
  techHeight = "techHeight", // Long
  techPixelAspectRatio = "techPixelAspectRatio", // Double
  techBitDepth = "techBitDepth", // Long
  techAudioBitDepth = "techAudioBitDepth", // Long
  techAudioSampling = "techAudioSampling", // Long
  imageAspectRatio = "imageAspectRatio", // String
  audioChannels = "audioChannels", // List<AudioChannels>
  tcDf = "tcDf", // Bool
  tcIn = "tcIn", // String
  tcOut = "tcOut", // String
  tcSOM = "tcSOM", // String
  tcEOM = "tcEOM", // String
  ingestResultMessage = "ingestResultMessage", // String
  storedFileName = "storedFileName", // String
  ingestWorkflowCount = "ingestWorkflowCount", // Long
  ingestWorkflowLastPurgeMessage = "ingestWorkflowLastPurgeMessage", // String
  lastIngestUpdateTime = "lastIngestUpdateTime", // DateTime
  ingestQCAllowBypass = "ingestQCAllowBypass", // Bool
  ingestHdrChecksBypass = "ingestHdrChecksBypass", // Bool
  billingStatus = "billingStatus", // String
  btId = "btId", // Long
  editReason = "editReason", // String
  isLegacyImport = "isLegacyImport", // Bool
  analysisDoNotUpdateAudioLocators = "analysisDoNotUpdateAudioLocators", // Bool
  logo2012Presence = "logo2012Presence", // Bool
  revision = "revision", // Long
  programType = "programType", // String
  secondaryTitle = "secondaryTitle", // String
  stereoscopicIsDirectorEye = "stereoscopicIsDirectorEye", // Bool
  subtitleFormat = "subtitleFormat", // String
  isTextlessAtTail = "isTextlessAtTail", // Bool
  imageIsBlackAndWhite = "imageIsBlackAndWhite", // Bool
  qcImageQuality = "qcImageQuality", // String (Bad, Correct, Good, Excellent)
  qcSoundQuality = "qcSoundQuality", // String (Bad, Correct, Good, Excellent)
  qcComment = "qcComment", // String
  titleInPicture = "titleInPicture", // String
  photonInfoReport = "photonInfoReport", // List<string>
  photonWarnReport = "photonWarnReport", // List<string>
  photonErrorReport = "photonErrorReport", // List<string>
  imfCplNames = "imfCplNames", // List<string>
  imfFileContents = "imfFileContents", // List<string>
  imfUuid = "imfUuid", // String
  imfAnnotation = "imfAnnotation", // String
  imfIssuer = "imfIssuer", // String
  imfIssueDate = "imfIssueDate", // DateTime
  dcpUuid = "dcpUuid", // String
  dcpCplUuid = "dcpCplUuid", // String
  dcpKdmIssueDate = "dcpKdmIssueDate", // DateTime
  dcpKdmStartDate = "dcpKdmStartDate", // DateTime
  dcpKdmEndDate = "dcpKdmEndDate", // DateTime
  dcpKdmRecipientName = "dcpKdmRecipientName", // String
  dcpKdmRecipientId = "dcpKdmRecipientId", // Long
  dcpKdmIsInternalDkdm = "dcpKdmIsInternalDkdm", // Bool
  imageSequencePattern = "imageSequencePattern", // String
  reelFrameCount = "reelFrameCount", // Long
  endFrameName = "endFrameName", // String
  startFrameName = "startFrameName", // String
  sysStorageStaggingName = "sysStorageStaggingName", // String (used to define stagging storage by name on ingest process)
  sysStorageArchiveName = "sysStorageArchiveName", // String (used to define archive storage by name on ingest process)
  sysStorageArchiveOnly = "sysStorageArchiveOnly", // Bool (used to define archive only and not standardandwork)
  sequenceSegments = "sequenceSegments", // List<SequenceMetadataSegment>
  reelNumber = "reelNumber", // String
  connectId = "connectId", // Long
  originalSupport = "originalSupport", // String
  vendorId = "vendorId", // String
  partnerId = "partnerId", // String
  isVirtual = "isVirtual", // Bool
  ingestQCBypassed = "ingestQCBypassed", // DateTime
  normalizerStatus = "normalizerStatus", // String
  fullQcDone = "fullQcDone", // Bool
  vodRatingsDescriptor = "vodRatingsDescriptor", // DbRatingDescriptorMetadataValueInfos
  vodRatings = "vodRatings", // DbLocalizedRatingMultiMetadataValueInfos
  vodRatingsReasons = "vodRatingsReasons", // DbLocalizedRatingReasonsMultiMetadataValueInfos
  vodGenres = "vodGenres", // List<Genre>
  vodClearForSale = "vodClearForSale", // DbLocalizedBoolMultiMetadataValueInfos
  vodClearForVod = "vodClearForVod", // DbLocalizedBoolMultiMetadataValueInfos
  vodSaleStartDate = "vodSaleStartDate", // DbLocalizedDateTimeMultiMetadataValueInfos
  vodSaleEndDate = "vodSaleEndDate", // DbLocalizedDateTimeMultiMetadataValueInfos
  vodPreorderSaleStartDate = "vodPreorderSaleStartDate", // DbLocalizedDateTimeMultiMetadataValueInfos
  vodVodStartDate = "vodVodStartDate", // DbLocalizedDateTimeMultiMetadataValueInfos
  vodVodEndDate = "vodVodEndDate", // DbLocalizedDateTimeMultiMetadataValueInfos
  vodTheatricalReleaseDate = "vodTheatricalReleaseDate", // DbLocalizedDateTimeMultiMetadataValueInfos
  vodPhysicalReleaseDate = "vodPhysicalReleaseDate", // DbLocalizedDateTimeMultiMetadataValueInfos
  vodTitleCredits = "vodTitleCredits", // String - VOD
  vodTitles = "vodTitles", // DbLocalizedTitlesMultiMetadataValueInfos - VOD
  vodDuration = "vodDuration", // Long
  vodCopyrights = "vodCopyrights", // StringMulti
  vodStudios = "vodStudios", // StringMulti
  vodAwards = "vodAwards", // StringMulti
  vodKeywords = "vodKeywords", // StringMulti
  vodCountriesOfOrigin = "vodCountriesOfOrigin", // TerritoryMulti
  vodCountriesOfProduction = "vodCountriesOfProduction", // TerritoryMulti
  titleOriginalCountry = "titleOriginalCountry", // TerritoryCode
  titleOriginalLocalizedLanguage = "titleOriginalLocalizedLanguage", // Language
  productionCompanies = "productionCompanies", // List<string>
  synopses = "synopses", // List<Synopsis>
  casts = "casts", // List<Cast>
  imdbId = "imdbId", // String
  imdbPoster = "imdbPoster", // String
  materialId = "materialId", // String
  speechToText = "speechToText", // SpeechToTextMetadataInfos
  bundlePackageType = "bundlePackageType", // String
  physicalItemMovments = "physicalItemMovments", // List<physicalItemMovment>
  originalAssetId = "originalAssetId", // String
  elementType = "elementType", // String
  supportId = "supportId", // Long
  support = "support", // String
  standardId = "standardId", // String
  standard = "standard", // String
  typeId = "typeId", // String
  type = "type", // String
  categoryId = "categoryId", // String
  category = "category", // String
  imageFormatId = "ImageFormatId", // String
  imageFormat = "imageFormat", // String
  videoFormatId = "VideoFormatId", // String
  videoFormat = "videoFormat", // String
  tapeDuration = "tapeDuration", // Long
  tapeBrand = "tapeBrand", // String
  tapeBrandId = "tapeBrandId", // String
  ltc = "ltc", // Bool
  vitc = "vitc", // Bool
  astc = "astc", // Bool
  field = "field", // String
  logoBrandName = "logoBrandName", // String
  inventoryDate = "inventoryDate", // String
  openingCreditsId = "openingCreditsId", // String
  openingCredits = "openingCredits", // String
  endCreditsId = "endCreditsId", // String
  endCredits = "endCredits", // String
  verificationDate = "verificationDate", // String
  qualityId = "qualityId", // String
  quality = "quality", // String
  vinaigre = "vinaigre", // Long
  boxes = "boxes", // Long
  imageId = "imageId", // String
  image = "image", // String
  copyNumber = "copyNumber", // String
  blId = "blId", // String
  identificationString = "identificationString", // String
  pallet = "pallet", // Long
  box = "box", // Long
  ownerId = "ownerId", // String
  broadcasterId = "broadcasterId", // String
  production = "production", // String
  dataStorageUnit = "dataStorageUnit", // String
  fileSize = "fileSise", // String
  nitrate = "nitrate", // Bool
  rackLabel = "rackLabel", // String
  rightsOwner = "rightsOwner", // String
  rightsEndDate = "rightsEndDateDate", // String
  localization = "localization", // String
  subCheckConformityStl = "subCheckConformityStl", // Bool
  subCheckConformityScc = "subCheckConformityScc", // Bool
  customerCodeId = "customerCodeId", // String
  primaryTitle = "primaryTitle", // String
  boxId = "boxId", // Long
  filing = "filing", // String
  episodeTitles = "episodeTitles", // StringMulti
  customerId = "customerId", // Long
  physicalItemType = "physicalItemType", // String
  imagePixelMatcher = "imagePixelMatcher", // String
  bundleBroadcasterId = "bundleBroadcasterId", // Long
  aspUploadToken = "aspUploadToken", // String
  dolbyAtmosPackageType = "dolbyAtmosPackageType", // String
  dolbyAtmosSoftware = "dolbyAtmosSoftware", // String
  dolbyAtmosNbOfObjects = "dolbyAtmosNbOfObjects", // Long
  assetKeywords = "assetKeywords", // String Multi
  episodesKeywords = "episodesKeywords", // String Multi by Episode
  mezzanifierStatus = "mezzanifierStatus", // String
  mezzanifierChecks = "mezzanifierChecks", // TBD
  mezzanifierBypass = "mezzanifierBypass", // Bool
  qcAutoStatus = "qcAutoStatus", // String
  assetPendingMetadata = "assetPendingMetadata", // String Multi
  bundleTitleStatus = "bundleTitleStatus", // String
  vodRightHolder = "vodRightHolder", // String
  vodLab = "vodLab", // String
  vodProvider = "vodProvider", // String
  virtualHeadChildrenIds = "virtualHeadChildrenIds", // Long Multi
  md5 = "md5", //String
  resume = "resume", //String
  otherVideoFormat = "otherVideoFormat", //String
  sysStorageTier = "sysStorageTier", //String
  assetOriginalLocalizedLanguage = "assetOriginalLocalizedLanguage", //LanguageCode

  //#region Adam system
  rc_prg_provenance = "rc_prg_provenance", // String
  rc_prg_media_id = "rc_prg_media_id", // String
  rc_prg_media_partie = "rc_prg_media_partie", // String (represents long)
  rc_prg_reserver = "rc_prg_reserver", // Bool
  rc_ba_id = "rc_ba_id", // String (Trailer Id)
  rc_prg_id = "rc_prg_id", // String (Source Id)
  sysStorageDiskPurgeTargetDate = "sysStorageDiskPurgeTargetDate", // Date (purge date)
  //#endregion Adam system
}
